import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/supabase";
import { formatDateTime } from "@/lib/ui";
import {
  Calendar, StickyNote, CheckSquare, CheckCircle, FileText, MessageCircle,
  ClipboardList, Clipboard, UserPlus, Activity, Circle,
} from "lucide-react";

const ICONS = {
  appointment: Calendar,
  visit: Activity,
  note: StickyNote,
  "task.created": CheckSquare,
  "task.completed": CheckCircle,
  document: FileText,
  communication: MessageCircle,
  form: ClipboardList,
  "intake.started": Clipboard,
  "intake.completed": CheckCircle,
  "client.created": UserPlus,
};

const COLORS = {
  appointment: "text-blue-600 bg-blue-50",
  visit: "text-purple-600 bg-purple-50",
  note: "text-amber-600 bg-amber-50",
  "task.created": "text-indigo-600 bg-indigo-50",
  "task.completed": "text-green-600 bg-green-50",
  document: "text-orange-600 bg-orange-50",
  communication: "text-teal-600 bg-teal-50",
  form: "text-pink-600 bg-pink-50",
  "intake.started": "text-slate-600 bg-slate-100",
  "intake.completed": "text-green-600 bg-green-50",
  "client.created": "text-[#E8712C] bg-[#FDE9D8]",
};

export default function TimelineTab({ clientId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/clients/${clientId}/timeline`);
      setEvents(r.events || []);
    } catch (e) {
      setEvents([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const FILTERS = [
    { key: "all", label: "All events" },
    { key: "appointment", label: "Appointments" },
    { key: "visit", label: "Visits" },
    { key: "note", label: "Notes" },
    { key: "task", label: "Tasks" },
    { key: "document", label: "Documents" },
    { key: "communication", label: "Communications" },
    { key: "form", label: "Forms" },
  ];

  const filtered = filter === "all"
    ? events
    : events.filter((e) => e.kind === filter || e.kind.startsWith(`${filter}.`));

  return (
    <div className="space-y-4" data-testid="timeline-tab">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key}
            data-testid={`timeline-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === f.key
                ? "bg-[#1C1917] text-white"
                : "bg-white border border-[#E8E2D9] text-[#57534E] hover:bg-[#FAF8F5]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-sm text-[#57534E]">Loading timeline…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#E8E2D9]">
          <Activity size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
          <p className="text-sm font-medium">Nothing to show yet</p>
          <p className="text-xs text-[#57534E] mt-1">
            Everything that happens with this client will appear here automatically.
          </p>
        </div>
      ) : (
        <div className="relative bg-white rounded-2xl border border-[#E8E2D9] p-6">
          <div className="absolute left-[38px] top-6 bottom-6 w-px bg-[#E8E2D9]" />
          <ul className="space-y-5">
            {filtered.map((e, i) => {
              const Icon = ICONS[e.kind] || Circle;
              const color = COLORS[e.kind] || "text-[#57534E] bg-[#F5EDE4]";
              return (
                <li key={`${e.id}-${i}`} className="relative flex gap-4" data-testid={`tl-event-${e.kind}`}>
                  <div className={`w-11 h-11 rounded-2xl ${color} flex items-center justify-center relative z-10 flex-shrink-0`}>
                    <Icon size={18} strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <div className="text-sm font-medium text-[#1C1917]">{e.title}</div>
                      <div className="text-xs text-[#A8A29E] whitespace-nowrap">{formatDateTime(e.at)}</div>
                    </div>
                    <EventMeta kind={e.kind} meta={e.meta} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function EventMeta({ kind, meta }) {
  if (!meta || Object.keys(meta).length === 0) return null;
  const parts = [];
  if (meta.type) parts.push(meta.type);
  if (meta.status) parts.push(meta.status);
  if (meta.priority) parts.push(`priority: ${meta.priority}`);
  if (meta.duration) parts.push(`${meta.duration} min`);
  if (meta.by) parts.push(`by ${meta.by}`);
  if (meta.due) parts.push(`due ${meta.due}`);
  if (meta.subject) parts.push(meta.subject);

  return (
    <div className="text-xs text-[#57534E] mt-0.5 space-y-1">
      {parts.length > 0 && <div className="capitalize">{parts.join(" · ")}</div>}
      {meta.body && <div className="text-[#57534E] whitespace-pre-wrap line-clamp-3">{meta.body}</div>}
      {meta.summary && <div className="text-[#57534E] whitespace-pre-wrap line-clamp-3">{meta.summary}</div>}
    </div>
  );
}
